import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NativeSelect,
  NativeSelectOption,
} from '@repo/shared-client/components/ui'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { PlayCircleIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  AvailableJobsQuery,
  AvailableJobsQueryVariables,
  TriggerJobMutation,
  TriggerJobMutationVariables,
} from 'types/graphql'

const AVAILABLE_JOBS_QUERY: TypedDocumentNode<AvailableJobsQuery, AvailableJobsQueryVariables> = gql`
  query AvailableJobsQuery {
    availableJobs {
      name
      description
      canTrigger
    }
  }
`

const TRIGGER_JOB_MUTATION: TypedDocumentNode<TriggerJobMutation, TriggerJobMutationVariables> = gql`
  mutation TriggerJobMutation($jobName: String!) {
    triggerJob(jobName: $jobName) {
      success
      message
    }
  }
`

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/jobs')({
  component: JobTriggerPage,
  head: () =>
    seo({
      title: `Admin - Job Trigger | ${env.VITE_APP_NAME}`,
      description: 'Admin Job Trigger Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, AVAILABLE_JOBS_QUERY)
    return { queryRef }
  },
})

function JobTriggerPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<AvailableJobsQuery>)

  const [triggerJob, { loading }] = useMutation(TRIGGER_JOB_MUTATION, {
    onCompleted: (mutationData) => {
      if (mutationData.triggerJob.success) {
        toast.success(mutationData.triggerJob.message)
      } else {
        toast.error(mutationData.triggerJob.message)
      }
    },
    onError: (mutationError) => {
      toast.error(t('Failed to trigger job: {{message}}', { message: mutationError.message }))
    },
  })

  const [selectedJobName, setSelectedJobName] = useState<string>('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />

  const availableJobs = data?.availableJobs || []

  const selectedJob = availableJobs.find((job) => job.name === selectedJobName)

  const handleTrigger = () => {
    if (selectedJobName) {
      triggerJob({ variables: { jobName: selectedJobName } })
    }
  }

  const handleConfirm = () => {
    handleTrigger()
    setIsDialogOpen(false)
  }

  if (availableJobs.length === 0) {
    return (
      <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex flex-col gap-2">
            <h1 className="font-semibold text-2xl tracking-tight">{t('Job Trigger')}</h1>
            <p className="text-muted-foreground">{t('Manually trigger background jobs for immediate execution')}</p>
          </div>
          <div className="flex w-full py-8">
            <p className="text-lg text-muted-foreground">{t('No triggerable jobs available')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="font-semibold text-2xl tracking-tight">{t('Job Trigger')}</h1>
          <p className="text-muted-foreground">{t('Manually trigger background jobs for immediate execution')}</p>
        </div>

        <div className="flex w-full flex-col gap-4">
          {loading ? (
            <Card className="w-full border-primary/30 bg-primary/5">
              <CardContent className="py-3 text-center">{t('Triggering job...')}</CardContent>
            </Card>
          ) : null}

          <Card className="w-full">
            <CardHeader>
              <CardTitle>{t('Select Job to Trigger')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex w-full flex-col gap-2">
                <span className="font-medium text-muted-foreground text-sm">{t('Job')}</span>
                <NativeSelect
                  className="w-full"
                  onChange={(e) => setSelectedJobName(e.target.value)}
                  value={selectedJobName}
                >
                  <NativeSelectOption value="">{t('Select a job...')}</NativeSelectOption>
                  {availableJobs.map((job) => (
                    <NativeSelectOption key={job.name} value={job.name}>
                      {job.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>

              {selectedJob ? (
                <div className="flex w-full flex-col gap-2">
                  <span className="font-medium text-muted-foreground text-sm">{t('Description')}</span>
                  <Card className="bg-muted">
                    <CardContent>
                      <p className="text-base">{selectedJob.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ) : null}

              <div className="flex w-full justify-end gap-3">
                <Button
                  disabled={!(selectedJobName && selectedJob?.canTrigger)}
                  onClick={() => setIsDialogOpen(true)}
                  type="button"
                >
                  <PlayCircleIcon />
                  {t('Trigger Job')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Dialog onOpenChange={setIsDialogOpen} open={isDialogOpen}>
            <DialogContent initialFocus={false}>
              <DialogHeader>
                <DialogTitle>{t('Confirm Job Trigger')}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-3 py-2">
                <p>{t('Are you sure you want to trigger the following job?')}</p>
                {selectedJob ? (
                  <Card className="border-destructive/40 bg-destructive/10">
                    <CardContent className="flex flex-col gap-1 py-3">
                      <p className="font-bold">{selectedJob.name}</p>
                      <p className="text-sm">{selectedJob.description}</p>
                    </CardContent>
                  </Card>
                ) : null}
                <p className="text-muted-foreground text-sm">{t('This will queue the job for immediate execution.')}</p>
              </div>
              <DialogFooter>
                <Button onClick={() => setIsDialogOpen(false)} type="button" variant="outline">
                  {t('Cancel')}
                </Button>
                <Button onClick={handleConfirm} type="button">
                  <PlayCircleIcon />
                  {t('Trigger Job')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
